/**
 * Country switcher dropdown. Lives in the homepage header next to the
 * AccountLink. On change it calls setCountry() in CountryContext, which
 * persists the choice to localStorage and refetches pricing.
 *
 * Country list is fetched from /api/intl/tiers so admin-controlled lists
 * stay in one place server-side.
 */
import { useEffect, useState } from "react";
import { Globe } from "lucide-react";
import axios from "axios";
import { useCountry } from "../contexts/CountryContext";
import { COUNTRY_LABELS } from "../lib/countries";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CountrySwitcher = ({ className = "", testIdPrefix = "country-switcher" }) => {
  const { country, setCountry } = useCountry();
  const [groups, setGroups] = useState(null);

  useEffect(() => {
    axios.get(`${API}/intl/tiers`)
      .then((r) => setGroups(r.data?.tiers || null))
      .catch(() => setGroups(null));
  }, []);

  const options = [];
  options.push({ value: "IN", label: COUNTRY_LABELS.IN, group: "India" });
  if (groups) {
    Object.entries(groups).forEach(([tierId, t]) => {
      (t.countries || []).forEach((c) => {
        options.push({
          value: c,
          label: COUNTRY_LABELS[c] || c,
          group: t.label,
        });
      });
    });
  }

  return (
    <label
      className={`inline-flex items-center gap-1.5 text-sm text-[#6B6B6B] ${className}`}
      data-testid={`${testIdPrefix}-label`}
    >
      <Globe size={16} strokeWidth={1.6} />
      <select
        value={country}
        onChange={(e) => setCountry(e.target.value)}
        className="bg-transparent border-none focus:outline-none focus:ring-0 text-sm text-[#1A1A1A] cursor-pointer pr-1"
        data-testid={`${testIdPrefix}-select`}
        aria-label="Select your country"
      >
        <optgroup label="India">
          <option value="IN">{COUNTRY_LABELS.IN}</option>
        </optgroup>
        {groups && Object.entries(groups).map(([tierId, t]) => (
          <optgroup key={tierId} label={t.label}>
            {(t.countries || []).map((c) => (
              <option key={c} value={c}>{COUNTRY_LABELS[c] || c}</option>
            ))}
          </optgroup>
        ))}
      </select>
    </label>
  );
};

export default CountrySwitcher;
